import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Card, CardHeader, CardDescription, CardContent } from "../ui/Card";
import { Button } from "../ui/Button";

export function KpiCard({ label, value, sublabel }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-2xl font-semibold">{value}</p>
        {sublabel && <p className="text-xs text-muted-foreground mt-1">{sublabel}</p>}
      </CardContent>
    </Card>
  );
}

export function TablePager({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-3 pt-3 border-t">
      <span className="text-xs text-muted-foreground">
        Page {page} of {totalPages}
      </span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
          Next
        </Button>
      </div>
    </div>
  );
}

const EMPTY_ROWS = [];

export function usePagedRows(rows = EMPTY_ROWS, { pageSize = 10 } = {}) {
  const list = rows || EMPTY_ROWS;
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [list]);
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  return {
    page: currentPage,
    setPage,
    totalPages,
    pageRows: list.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    total: list.length,
  };
}

export function usePagedFetch(fetcher, params, { pageSize = 10, enabled = true } = {}) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const filterKey = JSON.stringify(params);
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);

  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setPage(1);
    setRows([]);
    setTotal(0);
  }

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher({ ...params, offset: (page - 1) * pageSize, limit: pageSize })
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows);
        setTotal(res.total);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page, enabled, pageSize]);

  return {
    page,
    setPage,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
    rows,
    total,
    loading,
    error,
  };
}

export function MultiSelect({
  values,
  options,
  onChange,
  allLabel,
  formatValue = (value) => value,
  summaryLabel = (count) => `${count} selected`,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const selected = values.map(String);
  const label =
    selected.length === 0 ? allLabel : selected.length === 1 ? formatValue(selected[0]) : summaryLabel(selected.length);
  const title = selected.length === 0 ? allLabel : selected.map((value) => formatValue(value)).join(", ");
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? options.filter((option) => formatValue(String(option)).toLowerCase().includes(needle))
    : options;

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    searchRef.current?.focus();
    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = (value) => {
    const key = String(value);
    onChange(selected.includes(key) ? selected.filter((item) => item !== key) : [...selected, key]);
  };

  return (
    <div ref={containerRef} className={`relative ${className || ""}`}>
      <button
        type="button"
        className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:border-blue-500"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="truncate" title={title}>{label}</span>
        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 min-w-full w-max max-w-xs max-h-72 overflow-auto rounded-md border border-gray-200 bg-white text-gray-900 shadow-lg py-2"
          role="listbox"
          aria-multiselectable="true"
        >
          <div className="sticky top-0 bg-white px-2 pb-1">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <label className="flex items-start gap-3 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0"
              checked={selected.length === 0}
              onChange={() => onChange([])}
            />
            <span className="leading-5">{allLabel}</span>
          </label>
          {visible.map((option) => {
            const value = String(option);
            const isSelected = selected.includes(value);
            return (
              <label
                key={value}
                className="flex items-start gap-3 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0"
                  checked={isSelected}
                  onChange={() => toggle(value)}
                />
                <span className="leading-5">{formatValue(value)}</span>
              </label>
            );
          })}
          {visible.length === 0 && <p className="px-3 py-2 text-sm text-gray-500">No matches</p>}
        </div>
      )}
    </div>
  );
}

export function CreatableSelect({ value, options, onChange, onCreate, placeholder, className }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const text = String(value || "");
  const needle = text.trim().toLowerCase();
  const visible = needle
    ? options.filter((option) => String(option).toLowerCase().includes(needle))
    : options;
  const exact = options.some((option) => String(option).toLowerCase() === needle);
  const canCreate = Boolean(needle) && !exact;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const choose = (next, created = false) => {
    onChange(next);
    if (created) onCreate?.(next);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={text}
        placeholder={placeholder}
        className={className}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div
          className="absolute z-50 mt-1 min-w-full w-max max-w-xs max-h-72 overflow-auto rounded-md border border-gray-200 bg-white text-gray-900 shadow-lg py-1"
          role="listbox"
        >
          {visible.map((option) => (
            <button
              type="button"
              key={String(option)}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
              onMouseDown={(event) => {
                event.preventDefault();
                choose(String(option));
              }}
            >
              {option}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
              onMouseDown={(event) => {
                event.preventDefault();
                choose(text.trim(), true);
              }}
            >
              {`Add "${text.trim()}"`}
            </button>
          )}
          {visible.length === 0 && !canCreate && <p className="px-3 py-2 text-sm text-gray-500">No matches</p>}
        </div>
      )}
    </div>
  );
}
